import * as React from "react";
import Hyperlink from "source/app/presentation/Shared/Link/Hyperlink";
import { StyleClass, TCSS } from "source/app/presentation/Shared/Styles/CSS";
import { Sounds } from "source/app/services/Sound/Sounds";
import { GotoNodeAsync } from "source/app/services/Workflows/GotoNode";
import { BookOfMindQuote } from "motw_core/DTOs/BookOfMindQuote";
import { SetBookOfMindState } from "../Helpers/BookOfMindHelpers";
import {
    DisplayQuote,
    wordRevealDelayInMilliseconds,
    wordRevealDurationInMilliseconds,
    wordRevealInitialDelayInMilliseconds,
} from "./DisplayQuote";
import * as styles from "./DisplayBookOfMindQuote.css";
import { JumpToParent } from "../JumpToParent/JumpToParent";
import { workflowRepository } from "motw_core/Repositories/WorkflowRepository";
import ClientDataProvider from "source/app/services/External/ClientDataProvider/ClientDataProvider";

export function DisplayBookOfMindQuote({
    bomq,
    index,
    isSelected,
    wordRevealOffset,
    spokenQuoteIndex,
    spokenWordStartIndex,
    spokenWordEndIndex,
}: {
    bomq: BookOfMindQuote;
    index: number;
    isSelected: boolean;
    wordRevealOffset?: number;
    spokenQuoteIndex?: number;
    spokenWordStartIndex?: number;
    spokenWordEndIndex?: number;
}) {
    const isBeingSpoken = spokenQuoteIndex === index;
    const [isQuoteRevealComplete, setIsQuoteRevealComplete] =
        React.useState(wordRevealOffset === undefined);
    const [parentCollectionName, setParentCollectionName] = React.useState<
        string | undefined
    >(undefined);
    const [quoteParentName, setQuoteParentName] = React.useState(bomq.parent);

    React.useEffect(() => {
        if (wordRevealOffset === undefined) {
            setIsQuoteRevealComplete(true);
            return;
        }

        setIsQuoteRevealComplete(false);
        const quoteWordCount = bomq.quote.match(/\S+/g)?.length ?? 0;
        const revealCompleteDelay =
            wordRevealInitialDelayInMilliseconds +
            (wordRevealOffset + quoteWordCount) *
                wordRevealDelayInMilliseconds +
            wordRevealDurationInMilliseconds;
        const timeoutId = window.setTimeout(
            () => setIsQuoteRevealComplete(true),
            revealCompleteDelay
        );

        return () => window.clearTimeout(timeoutId);
    }, [bomq, wordRevealOffset]);

    React.useEffect(() => {
        let isCurrentQuote = true;
        setQuoteParentName(bomq.parent);

        if (bomq.parent) {
            return () => {
                isCurrentQuote = false;
            };
        }

        workflowRepository.GetNodeById(ClientDataProvider, bomq.id).then(
            (quoteNode) =>
                quoteNode
                    ? workflowRepository.GetParentNodeAsync(
                          ClientDataProvider,
                          quoteNode
                      )
                    : undefined
        ).then((quoteParentNode) => {
            if (isCurrentQuote && quoteParentNode) {
                setQuoteParentName(quoteParentNode.name);
            }
        });

        return () => {
            isCurrentQuote = false;
        };
    }, [bomq]);

    React.useEffect(() => {
        let isCurrentQuote = true;
        setParentCollectionName(undefined);

        if (!isSelected) {
            return () => {
                isCurrentQuote = false;
            };
        }

        workflowRepository.GetNodeById(ClientDataProvider, bomq.id).then(
            (quoteNode) =>
                quoteNode
                    ? workflowRepository.GetParentNodeAsync(
                          ClientDataProvider,
                          quoteNode
                      )
                    : undefined
        ).then((quoteParentNode) => {
            if (!isCurrentQuote || !quoteParentNode) {
                return;
            }

            setQuoteParentName(quoteParentNode.name);

            workflowRepository
                .GetParentNodeAsync(ClientDataProvider, quoteParentNode)
                .then((parentNode) => {
                    if (isCurrentQuote) {
                        setParentCollectionName(parentNode?.name);
                    }
                });
        });

        return () => {
            isCurrentQuote = false;
        };
    }, [bomq, isSelected]);

    const extraEmphasisStyle: TCSS = {
        opacity: 1.0,
        textShadow: `0px 0px 5px gold`,
    };

    const quoteOuterStyle = StyleClass(`bookOfMindQuoteOuterStyle`, {
        margin: "10px 15px",
        textAlign: "left",
        textIndent: "15px",
        fontSize: `14px`,
        color: "lightgoldenrodyellow",
        opacity: 0.8,
        textDecoration: "inherit",
    });

    return (
        <div
            className={`${styles.quoteRow} ${
                isSelected ? styles.selectedQuoteRow : ""
            } ${isQuoteRevealComplete ? styles.quoteRevealComplete : ""}`}
        >
            <Hyperlink
                id={`scrollTarget_bomq_${index}`}
                classes={styles.quoteLink}
                whenClicked={() => {
                    SetBookOfMindState({
                        selectedQuote: isSelected ? undefined : bomq,
                    });
                    Sounds.bellDingDong.Play({ allowConcurrent: true });
                    GotoNodeAsync(bomq.quote);
                }}
            >
                <div
                    className={`${quoteOuterStyle} ${styles.quote}`}
                    style={isBeingSpoken ? extraEmphasisStyle : {}}
                >
                    <DisplayQuote
                        {...{
                            bomq,
                            index,
                            wordRevealOffset,
                            spokenQuoteIndex,
                            spokenWordStartIndex,
                            spokenWordEndIndex,
                            authorName: quoteParentName,
                            parentCollectionName,
                        }}
                    />
                </div>
            </Hyperlink>
            {isSelected && (
                <JumpToParent
                    selectedQuote={bomq}
                    quoteParentName={quoteParentName}
                    parentCollectionName={parentCollectionName}
                />
            )}
        </div>
    );
}
